import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  LayoutDashboard, Package, ClipboardList, GitBranch, Users,
  LogOut, Sun, Moon, Bell, Smartphone, Menu, X, Zap, ChevronRight, BookOpen, BarChart3, QrCode,
  ChevronLeft, Activity
} from 'lucide-react';
import MobileBottomNav from './MobileBottomNav';
import QRScannerModal from '../mobile/QRScannerModal';
import { systemAPI } from '../../utils/api';
import shortLogo from '../../img/shortlogo.png';
import fullLogo from '../../img/fulllogo.png';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', section: 'main', permission: 'dashboard' },
  { to: '/assets', icon: Package, label: 'Assets', section: 'main', permission: 'assets' },
  { to: '/jobs', icon: ClipboardList, label: 'Jobs', section: 'main', permission: 'jobs' },
  { to: '/workflows', icon: GitBranch, label: 'Workflows', section: 'main', permission: 'workflows' },
  { to: '/technician', icon: Smartphone, label: 'Tech View', section: 'field', permission: 'tech_view' },
  { to: '/users', icon: Users, label: 'Users', section: 'admin', permission: 'users' },
  { to: '/taxonomy', icon: Zap, label: 'Taxonomy', section: 'admin', permission: 'taxonomy' },
  { to: '/sops', icon: BookOpen, label: 'Procedures', section: 'admin', permission: 'sops' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', section: 'admin', permission: 'analytics' },
  { to: '/audit-logs', icon: Activity, label: 'System Audit', section: 'admin', superOnly: true },
];

const avatarColors = ['#0EA5E9','#8B5CF6','#10B981','#F97316','#EF4444','#F59E0B'];
const getColor = (name) => avatarColors[name?.charCodeAt(0) % avatarColors.length] || '#0EA5E9';
const getInitials = (name) => name?.split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase() || '??';

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(localStorage.getItem('sidebar-collapsed') === 'true');
  const [showScanner, setShowScanner] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Screen tracking
  useEffect(() => {
    if (user) {
      systemAPI.track({
        action: 'PAGE_VIEW',
        entity_type: 'SCREEN',
        details: { path: location.pathname, title: document.title }
      }).catch(() => {}); // Silent fail
    }
  }, [location.pathname, user?.id]);

  const filtered = navItems.filter(n => {
    if (user?.role === 'super_admin') return true;
    if (n.superOnly) return false;
    return !n.permission || user?.permissions?.includes(n.permission);
  });
  const sections = [...new Set(filtered.map(n => n.section))];
  const sectionLabels = { main: 'Main', field: 'Field', admin: 'Administration' };

  const pageTitle = {
    '/dashboard': { title: 'Dashboard', sub: 'Operations overview' },
    '/assets': { title: 'Assets', sub: 'Manage equipment & QR codes' },
    '/jobs': { title: 'Jobs', sub: 'Track field operations' },
    '/workflows': { title: 'Workflows', sub: 'Inspection templates' },
    '/technician': { title: 'Technician View', sub: 'Mobile field interface' },
    '/users': { title: 'Users', sub: 'Team management' },
    '/taxonomy': { title: 'Asset Taxonomy', sub: 'Classification hierarchy' },
    '/sops': { title: 'Procedures', sub: 'Standard Operating Procedures (SOPs)' },
    '/analytics': { title: 'Analytics & Reporting', sub: 'Performance & Compliance Dashboard' },
    '/audit-logs': { title: 'System Audit', sub: 'User activity & security logs' },
  }[location.pathname] || { title: 'FieldOps-Pro', sub: '' };

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleScanResult = (data) => {
    setShowScanner(false);
    // When an asset is scanned, technicians usually want to see either 
    // the asset jobs or the asset details. For now, navigate to Tech View.
    navigate('/technician');
  };

  const toggleSidebarCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', newState);
  };

  return (
    <div className={`app-layout ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Sidebar - Auto-hidden on mobile via index.css */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-mark" style={{ gap: 8, alignItems: 'center' }}>
            {isCollapsed ? (
              <div className="logo-icon" style={{ background: 'transparent', width: 42, height: 42, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={shortLogo} alt="FieldOps Pro" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', height: 42, overflow: 'hidden' }}>
                <img src={fullLogo} alt="FieldOps Pro" style={{ height: 36, maxWidth: 195, objectFit: 'contain' }} />
              </div>
            )}
          </div>
          <button 
            className="btn-icon show-mobile" 
            onClick={() => setSidebarOpen(false)} 
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--text-muted)' }}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* User profile moved to bottom */}

        <nav className="sidebar-nav">
          {sections.map(sec => (
            <div key={sec}>
              {!isCollapsed && <div className="sidebar-section-label">{sectionLabels[sec]}</div>}
              {filtered.filter(n => n.section === sec).map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  title={isCollapsed ? item.label : ''}
                >
                  <item.icon className="nav-icon" />
                  {!isCollapsed && item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar" style={{ background: getColor(user?.name) }}>
              {getInitials(user?.name)}
            </div>
            {!isCollapsed && (
              <div style={{ minWidth: 0 }}>
                <div className="sidebar-user-name">{user?.name}</div>
                <div className="sidebar-user-role">{user?.role}</div>
              </div>
            )}
          </div>

          <button className="nav-item" onClick={handleLogout} title={isCollapsed ? "Sign Out" : ""}>
            <LogOut className="nav-icon" />
            {!isCollapsed && "Sign Out"}
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)', zIndex:240 }}
          onClick={() => setSidebarOpen(false)} />
      )}

      <div className="main-content">
        {/* Header - Permanently hide on mobile for Job Details to ensure ZERO overlap with our custom professional header */}
        <header className={`header ${location.pathname.includes('/jobs/') ? 'hide-mobile' : ''}`}>
          <button className="collapse-toggle hide-mobile" style={{ marginRight: 8 }} onClick={toggleSidebarCollapse} title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>

          <button className="btn-icon" onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ display:'none' }} id="menu-btn" aria-label="Toggle navigation menu">
            <Menu size={18} />
          </button>

          <div 
            onClick={() => user?.role === 'technician' ? navigate('/technician') : navigate('/dashboard')}
            style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
            className="hover-opacity"
            title="Go to Home"
          >
            <div className="header-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="show-mobile" style={{ display: 'none' }}><img src={shortLogo} alt="Logo" style={{ width: 28, height: 28, objectFit: 'contain' }} /></span>
              {pageTitle.title}
            </div>
            <div className="header-subtitle hide-mobile">{pageTitle.sub}</div>
          </div>

          <div className="header-right">
            <button className="btn-icon" onClick={toggle} title="Toggle theme" aria-label="Toggle theme">
              {isDark ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <div className="hide-mobile" style={{
              padding:'5px 12px', borderRadius:'var(--r-full)',
              background:'var(--accent-muted)', color:'var(--accent)',
              fontSize:12, fontWeight:700, border:'1px solid rgba(14,165,233,0.2)'
            }}>
              {user?.role?.toUpperCase()}
            </div>
            <div 
              className="avatar avatar-md" 
              style={{ background: getColor(user?.name), fontSize:13, cursor:'pointer' }}
              onClick={() => navigate('/profile')}
              title="View Profile"
            >
              {getInitials(user?.name)}
            </div>
          </div>
        </header>

        <div className="page-body">
          <Outlet />
        </div>

        {/* Mobile Nav */}
        <MobileBottomNav onScanClick={() => setShowScanner(true)} />

        {/* Mobile Scanner Modal */}
        {showScanner && (
          <QRScannerModal 
            onClose={() => setShowScanner(false)} 
            onResult={handleScanResult} 
          />
        )}
      </div>

      <style>{`
        @media (max-width: 1024px) { 
          .hide-mobile { display: none !important; }
          #menu-btn { display: flex !important; } 
        }
        @keyframes scanLine {
          0% { top: 0; }
          100% { top: 100%; }
        }
      `}</style>
    </div>
  );
}
